import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Plus, Share2, Trash2, Wheat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';
import { useI18n } from '../i18n';

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
}

async function shareCard(harvest, plant, locale) {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 1080, 1080);
    gradient.addColorStop(0, '#173d2a');
    gradient.addColorStop(1, '#6f8f57');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1080, 1080);

    if (plant?.image_path) {
        try {
            const image = await loadImage(fileUrl(plant.image_path));
            const scale = Math.max(1080 / image.width, 670 / image.height);
            const width = image.width * scale;
            const height = image.height * scale;
            ctx.drawImage(image, (1080 - width) / 2, (670 - height) / 2, width, height);
            ctx.fillStyle = 'rgba(12,35,23,.25)';
            ctx.fillRect(0, 0, 1080, 670);
        } catch { /* branded card still works without a photo */ }
    }

    ctx.fillStyle = '#f7f3e9';
    ctx.fillRect(0, 650, 1080, 430);
    ctx.fillStyle = '#173d2a';
    ctx.font = '700 42px Georgia, serif';
    ctx.fillText('GrowDaily.fun', 72, 730);
    ctx.font = '700 72px Georgia, serif';
    ctx.fillText(`${harvest.quantity} ${harvest.unit}`, 72, 840);
    ctx.font = '48px Arial, sans-serif';
    ctx.fillText(harvest.plant_name, 72, 915);
    ctx.fillStyle = '#6a745f';
    ctx.font = '30px Arial, sans-serif';
    ctx.fillText(locale === 'da' ? 'Dyrket og høstet med GrowDaily 🌱' : 'Grown and harvested with GrowDaily 🌱', 72, 995);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) throw new Error('Could not create image');
    const file = new File([blob], `growdaily-harvest-${harvest.id}.jpg`, { type: 'image/jpeg' });
    const title = locale === 'da' ? `Min høst: ${harvest.plant_name}` : `My harvest: ${harvest.plant_name}`;
    const text = `${harvest.quantity} ${harvest.unit} ${harvest.plant_name} · GrowDaily.fun`;
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title, text, files: [file], url: 'https://growdaily.fun' });
        return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success(locale === 'da' ? 'Delingsbilledet er downloadet' : 'Share image downloaded');
}

export default function Harvest() {
    const navigate = useNavigate();
    const { locale } = useI18n();
    const da = locale === 'da';
    const [plants, setPlants] = useState([]);
    const [harvests, setHarvests] = useState([]);
    const [plantId, setPlantId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unit, setUnit] = useState('kg');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [sharingId, setSharingId] = useState(null);

    const load = async () => {
        const [p, h] = await Promise.all([api.get('/plants'), api.get('/harvest')]);
        setPlants(p.data || []);
        setHarvests(h.data || []);
    };
    useEffect(() => { load().catch(() => {}); }, []);
    const selected = useMemo(() => plants.find((p) => p.id === plantId), [plants, plantId]);

    const add = async (event) => {
        event.preventDefault();
        const amount = Number(quantity);
        if (!selected || !Number.isFinite(amount) || amount <= 0) return;
        setBusy(true);
        try {
            await api.post('/harvest', { plant_id: selected.id, plant_name: selected.name, quantity: amount, unit, notes: notes.trim() || undefined });
            setQuantity(''); setNotes('');
            await load();
            toast.success(da ? 'Høsten er registreret 🌱' : 'Harvest logged 🌱');
        } catch { toast.error(da ? 'Høsten kunne ikke gemmes' : 'Could not save harvest'); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        await api.delete(`/harvest/${id}`);
        setHarvests((items) => items.filter((item) => item.id !== id));
    };

    const share = async (harvest) => {
        setSharingId(harvest.id);
        try { await shareCard(harvest, plants.find((p) => p.id === harvest.plant_id), locale); }
        catch (error) { if (error?.name !== 'AbortError') toast.error(da ? 'Deling mislykkedes' : 'Sharing failed'); }
        finally { setSharingId(null); }
    };

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-5"><ArrowLeft size={14} /> {da ? 'Tilbage' : 'Back'}</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{da ? 'Høst & deling' : 'Harvest & sharing'}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1">{da ? 'Min høst' : 'My harvest'}</h1>
            <p className="text-sm text-brand-ink2 mt-2 mb-6">{da ? 'Registrér hvad haven giver, og del et automatisk GrowDaily-billede.' : 'Log what your garden produces and share an automatic GrowDaily image.'}</p>

            <form onSubmit={add} className="bg-brand-card border border-brand-border rounded-3xl p-5 space-y-3">
                <select value={plantId} onChange={(e) => setPlantId(e.target.value)} className="w-full bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-3 text-sm text-brand-ink" required>
                    <option value="">{da ? 'Vælg plante…' : 'Choose plant…'}</option>
                    {plants.map((plant) => <option key={plant.id} value={plant.id}>{plant.emoji || '🌱'} {plant.name}</option>)}
                </select>
                <div className="grid grid-cols-[1fr_110px] gap-2">
                    <input type="number" min="0.01" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder={da ? 'Mængde' : 'Amount'} className="bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-3 text-sm" required />
                    <select value={unit} onChange={(e) => setUnit(e.target.value)} className="bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-3 text-sm"><option>kg</option><option>g</option><option>stk.</option><option>pcs</option><option>L</option></select>
                </div>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={da ? 'Valgfri note' : 'Optional note'} className="w-full bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-3 text-sm" />
                <button disabled={busy || !plantId || !quantity} className="w-full bg-brand-green text-white rounded-full py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {da ? 'Registrér høst' : 'Log harvest'}</button>
            </form>

            <div className="mt-7 space-y-3">
                {harvests.length === 0 && <div className="text-center bg-brand-card border border-dashed border-brand-border rounded-3xl p-8 text-brand-ink3"><Wheat className="mx-auto mb-2" />{da ? 'Ingen høst registreret endnu.' : 'No harvest logged yet.'}</div>}
                {harvests.map((harvest) => (
                    <div key={harvest.id} className="bg-brand-card border border-brand-border rounded-2xl p-4 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-brand-green/10 flex items-center justify-center text-xl">{plants.find((p) => p.id === harvest.plant_id)?.emoji || '🥕'}</div>
                        <div className="flex-1 min-w-0"><div className="font-serif text-lg text-brand-ink truncate">{harvest.plant_name}</div><div className="text-xs text-brand-ink3">{harvest.quantity} {harvest.unit} · {new Date(harvest.harvested_at).toLocaleDateString(locale)}</div>{harvest.notes && <p className="text-xs text-brand-ink2 mt-1 truncate">{harvest.notes}</p>}</div>
                        <button onClick={() => share(harvest)} disabled={sharingId === harvest.id} className="p-2.5 rounded-full bg-brand-green/10 text-brand-green" title={da ? 'Del høst' : 'Share harvest'}>{sharingId === harvest.id ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}</button>
                        <button onClick={() => remove(harvest.id)} className="p-2 text-brand-ink3 hover:text-brand-terracotta" title={da ? 'Slet' : 'Delete'}><Trash2 size={15} /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}
